import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';

import { validateGeneral } from '@/utils/handleCheckKey';
import useOnClickOutside from '@/hooks/useClickOutside';
import { CloseSquare } from '@/icons/index';

import Loader from '../Loader/Loader';

import styles from './InputAutoComplete.module.scss';

const InputAutoComplete = ({
  className,
  data,
  placeholder = 'Baku',
  value,
  setValue = () => null,
  setOptionValue,
  cleanText,
  inputRef,
  label,
  onSelect,
  models,
  highlight,
  searchList,
  endIcon,
  endIconClick,
  onClick,
  loading,
  withoutPopulars,
  withoutIcons,
  animatedLabel,
  required,
  id,
  defaultModels,
  disabled,
}) => {
  const [searchResult, setSearchResult] = useState([]);
  const [active, setActive] = useState(false);

  const router = useRouter();
  const { t } = useTranslation();

  const outsideRef = useRef(null);

  const getTopValue = () => {
    switch (true) {
      case withoutPopulars:
        return '90%';
      case data?.countries:
        return 'calc(90% - 65px)';
      default:
        return 'auto';
    }
  };

  const topValue = getTopValue();

  useEffect(() => {
    setValue('');
  }, [router.isReady]);

  const highlightText = (text) => {
    if (!value) return text;
    const regex = new RegExp(`(${value})`, 'gi');
    return text?.replace(regex, '<span class="highlight">$1</span>');
  };

  const inputOnChange = (e) => {
    const inputValue = e.target.value;
    setValue(inputValue);
    if (inputValue === '') {
      setValue(inputValue);
      setActive(false);
      setSearchResult([]);
      // reset();
      return;
    }

    const result = data?.countries?.filter((item) => {
      if (
        (item.name.startsWith('İ')
          ? item.name.toLowerCase().replace('i̇', 'i')
          : item.name.toLowerCase()
        ).indexOf(inputValue.toLowerCase()) !== -1
      ) {
        return item.name;
      }
      return null;
    });
    const resultModels = models?.filter((item) => {
      return item?.name?.toLowerCase().includes(inputValue.toLowerCase());
    });
    setSearchResult(result || (defaultModels ? models : resultModels));
    setActive(true);
  };
  const popularCountries = (item) => {
    return (
      <span
        className={styles.country}
        key={item?.id}
        onClick={() => onSelect(item?.name)}
        itemScope
        itemType='https://schema.org/Text'
      >
        {item?.name}
      </span>
    );
  };

  const renderSearchContent = (item) => {
    return (
      <span
        className={styles.country}
        key={item?.title}
        onClick={() => onSelect(item)}
        dangerouslySetInnerHTML={{
          __html: highlightText(item?.title ?? item?.description),
        }}
      ></span>
    );
  };

  const renderOptions = (option, index) => {
    const handleSelect = () => {
      if (setValue) setValue(option.name);
      if (setOptionValue) setOptionValue(option);
      if (onSelect) onSelect(option.name);
      setActive(false);
      setSearchResult([]);
    };

    return (
      <li
        key={index}
        onClick={handleSelect}
        itemScope
        itemType='https://schema.org/ListItem'
      >
        <span className={styles.active}>
          {Array.isArray(active) ? active[0] : active}
        </span>
        <span
          dangerouslySetInnerHTML={{
            __html: highlight ? highlightText(option.name) : option.name,
          }}
        ></span>
      </li>
    );
  };
  useOnClickOutside(outsideRef, () => setActive(false));
  return (
    <div
      ref={outsideRef}
      className={classNames([
        styles.inputDropDown,
        className,
        { [styles.active]: active },
      ])}
      itemScope
      itemType='https://schema.org/SearchAction'
      // onBlur={() => setActive(false)}
    >
      <div className={styles.inputWrapper}>
        {!!label && !animatedLabel && (
          <span className={`label ${styles.inputLabel}`}>{label}</span>
        )}
        <input
          ref={inputRef}
          className={styles.input}
          onChange={inputOnChange}
          placeholder={placeholder}
          value={value}
          onFocus={() => setActive(true)}
          onClick={onClick}
          required={required}
          disabled={disabled}
          id={id}
          onKeyDown={validateGeneral}
          // Browsers on Android and IOS use  different engines which ones may handle input clearing and autocomplete differently!
          autoComplete='off'
          aria-label={label || placeholder}
        />
        {animatedLabel && <label htmlFor={id}>{label}</label>}
        <div className={styles.btnsWrapper}>
          {!!value && !withoutIcons && (
            <button
              aria-label={cleanText || 'Bakcell'}
              onClick={() => setValue('')}
              className={styles.close}
            >
              {cleanText || <CloseSquare />}
            </button>
          )}
          {!!endIcon && (
            <span className='endIcon' onClick={endIconClick}>
              {endIcon}
            </span>
          )}
        </div>
      </div>
      <ul
        className={classNames([
          styles.dropDown,
          {
            [styles.hidden]: !searchResult?.length,
          },
        ])}
        style={{ top: topValue }}
      >
        {searchResult?.map(renderOptions)}
      </ul>
      {!withoutPopulars && (
        <>
          {!!data?.bottom_countries_title && (
            <h6 className={styles.title}>{data?.bottom_countries_title}:</h6>
          )}
          <div className={styles.popularBox}>
            {data?.bottom_countries?.map(popularCountries)}
          </div>
        </>
      )}

      {searchList && searchList?.data?.length > 0 ? (
        <div className={styles.searchContentList}>
          {loading && <Loader />}
          {searchList?.data?.map(renderSearchContent)}
        </div>
      ) : (
        !data &&
        !loading &&
        !models &&
        value && <p className={styles.info}>{t('esim:common.notFound')}</p>
      )}
    </div>
  );
};

export default InputAutoComplete;
